import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  VStack,
  Divider,
  Text,
  HStack,
  Alert,
  AlertIcon,
  Box,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import { CreateTaskData } from '../../api/taskApi';
import { useClients } from '../../hooks/useClients';
import { useOrganizationEnums } from '../../hooks/useOrganizationEnums';
import { formatEnumLabel } from '../../utils/organizationEnums';
import { useProjects, useCreateProject } from '../../hooks/useProjects';
import { useUsers } from '../../hooks/useUsers';
import { useAuthenticatedUser } from '../../contexts/AuthenticatedUserInfoContext';
import { useToast } from '@chakra-ui/react';
import { PermissionGuard } from '../PermissionGuard';
import { Permission } from '../../utils/permissions';
import TaskForm from '../Forms/TaskForm';

interface TaskBuiltInFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  formData: CreateTaskData;
  onFormChange: (data: Partial<CreateTaskData>) => void;
  onSubmit: () => void;
  isLoading?: boolean;
  selectedProject?: any | null;
  onProjectChange?: (project: any | null) => void;
  title?: string;
  hideProjectField?: boolean;
}

const TaskBuiltInFormModal: React.FC<TaskBuiltInFormModalProps> = (props) => {
  // destructure as before
  const {
    isOpen,
    onClose,
    formData,
    onFormChange,
    onSubmit,
    isLoading = false,
    selectedProject = null,
    onProjectChange = () => {},
    title = 'Create New Task',
    hideProjectField = false,
  } = props;

  // State for workflow management
  const [step, setStep] = React.useState<'selectProject' | 'createProject' | 'createTask'>(
    hideProjectField ? 'createTask' : 'selectProject'
  );

  // Project creation form data
  const [projectFormData, setProjectFormData] = useState<{
    userWhoCreatedProject: string;
    projectName: string;
    type: string;
    client: string;
  }>({
    userWhoCreatedProject: '',
    projectName: '',
    type: 'software',
    client: '',
  });

  // Use hooks to fetch data
  const { data: clients = [] } = useClients();
  const { data: organizationEnums } = useOrganizationEnums();
  const { data: projects = [] } = useProjects();
  const { data: users = [] } = useUsers();
  const createProjectMutation = useCreateProject();
  const { authenticatedUser } = useAuthenticatedUser();
  const toast = useToast();

  // Initialize project form with user ID
  React.useEffect(() => {
    if (authenticatedUser?.LoggedInUserId) {
      setProjectFormData(prev => ({
        ...prev,
        userWhoCreatedProject: authenticatedUser.LoggedInUserId,
      }));
    }
  }, [authenticatedUser]);

  // Reset workflow when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      if (hideProjectField) {
        setStep('createTask');
      } else if (!selectedProject) {
        setStep('selectProject');
      } else {
        setStep('createTask');
      }
    }
  }, [isOpen, selectedProject, hideProjectField]);

  const handleProjectChange = (projectId: string) => {
    if (projectId === 'create-new') {
      setStep('createProject');
    } else if (projectId === '') {
      onProjectChange(null);
      setStep('selectProject');
    } else {
      const project = projects?.find((p: any) => p._id === projectId);
      if (project) {
        onProjectChange(project);
        setStep('createTask');
      }
    }
  };

  const handleCreateProject = async () => {
    if (!projectFormData.projectName.trim()) {
      toast({
        title: 'Error',
        description: 'Project name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const newProject = await createProjectMutation.mutateAsync(projectFormData);
      toast({
        title: 'Project Created',
        description: `Project "${projectFormData.projectName}" has been created successfully!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Select the newly created project
      onProjectChange(newProject);

      // Reset project form
      setProjectFormData({
        userWhoCreatedProject: authenticatedUser?.LoggedInUserId || '',
        projectName: '',
        type: 'software',
        client: '',
      });

      setStep('createTask');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create project. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const goBackToProjectSelection = () => {
    setStep('selectProject');
    onProjectChange(null);
  };

  // Handle user selection for authBy
  const handleUserToggle = (userId: string) => {
    const currentAuthBy = formData.authBy || [];
    const isSelected = currentAuthBy.includes(userId);

    if (isSelected) {
      // Remove user
      onFormChange({ authBy: currentAuthBy.filter(id => id !== userId) });
    } else {
      // Add user
      onFormChange({ authBy: [...currentAuthBy, userId] });
    }
  };

  const getSelectedUserNames = () => {
    if (!formData.authBy || formData.authBy.length === 0) return [];
    return formData.authBy.map(userId => {
      const user = users?.find(u => u._id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    });
  };

  const renderProjectSelection = () => (
    <VStack spacing={4}>
      <Alert status="info">
        <AlertIcon />
        Please select a project for this task. If the project doesn't exist, you can create a new
        one.
      </Alert>

      <FormControl isRequired>
        <FormLabel>Select Project</FormLabel>
        <Select
          value={selectedProject?._id || ''}
          onChange={e => handleProjectChange(e.target.value)}
          placeholder="Choose a project"
        >
          {projects?.map((project: any) => (
            <option key={project._id} value={project._id}>
              {project.projectNumber} - {project.projectName}
            </option>
          ))}
          <option value="create-new">+ Create New Project</option>
        </Select>
      </FormControl>
    </VStack>
  );

  const renderProjectCreation = () => (
    <VStack spacing={4}>
      <Alert status="info">
        <AlertIcon />
        Create a new project to associate with your task.
      </Alert>

      <FormControl isRequired>
        <FormLabel>Project Name</FormLabel>
        <Input
          value={projectFormData.projectName}
          onChange={e => setProjectFormData({ ...projectFormData, projectName: e.target.value })}
          placeholder="Enter project name"
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Project Type</FormLabel>
        <Select
          value={projectFormData.type}
          onChange={e => setProjectFormData({ ...projectFormData, type: e.target.value })}
        >
          {organizationEnums?.projectTypes.map((type) => (
            <option key={type} value={type}>
              {formatEnumLabel(type)}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl>
        <FormLabel>Client (optional)</FormLabel>
        <Select
          value={projectFormData.client || ''}
          onChange={e => setProjectFormData({ ...projectFormData, client: e.target.value })}
        >
          <option value="">No client</option>
          {clients?.map((client: any) => (
            <option key={client._id} value={client._id}>
              {client.clientName}
            </option>
          ))}
        </Select>
      </FormControl>
    </VStack>
  );

  const renderTaskCreation = () => (
    <VStack spacing={4}>
      {selectedProject && (
        <Box p={3} bg="green.50" borderRadius="md" w="100%">
          <Text fontSize="sm" fontWeight="bold" color="green.700">
            Task will be added to: {selectedProject.projectName}
          </Text>
          <Text fontSize="xs" color="green.600">
            Project Number: {selectedProject.projectNumber}
          </Text>
          <Text fontSize="xs" color="green.600">
            Client:{' '}
            {typeof selectedProject.client === 'string'
              ? selectedProject.client
              : selectedProject.client?.clientName || 'Unknown Client'}
          </Text>
        </Box>
      )}
      <TaskForm formData={formData} onFormChange={onFormChange} users={users} />
    </VStack>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh" overflowY="auto">
        <ModalHeader>
          {hideProjectField
            ? title
            : step === 'selectProject'
            ? 'Select Project for Task'
            : step === 'createProject'
            ? 'Create New Project'
            : title}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {hideProjectField
            ? renderTaskCreation()
            : step === 'selectProject'
            ? renderProjectSelection()
            : step === 'createProject'
            ? renderProjectCreation()
            : renderTaskCreation()}
        </ModalBody>
        <ModalFooter>
          {hideProjectField ? (
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={onSubmit} isLoading={isLoading}>
                {title?.includes('Edit') || title?.includes('Update') ? 'Update Task' : 'Create Task'}
              </Button>
            </HStack>
          ) : step === 'createProject' ? (
            <HStack spacing={3}>
              <Button variant="ghost" onClick={goBackToProjectSelection}>
                Back to Projects
              </Button>
              <PermissionGuard permission={Permission.CREATE_PROJECTS}>
                <Button
                  colorScheme="green"
                  onClick={handleCreateProject}
                  isLoading={createProjectMutation.isPending}
                >
                  Create Project
                </Button>
              </PermissionGuard>
            </HStack>
          ) : step === 'createTask' ? (
            <HStack spacing={3}>
              <Button variant="ghost" onClick={goBackToProjectSelection}>
                Change Project
              </Button>
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={onSubmit} isLoading={isLoading}>
                {title?.includes('Edit') || title?.includes('Update') ? 'Update Task' : 'Create Task'}
              </Button>
            </HStack>
          ) : step === 'selectProject' ? (
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TaskBuiltInFormModal;
